import { commonUtils } from '@/helpers/commonUtils'
import styles from './StepOne.module.less'
import { CSSProperties, MouseEventHandler, useEffect, useState } from 'react'
import AButton from '@/components/A/AButton/AButton'
import AIcon from '@/components/A/AIcon/AIcon'
import ASelect from '@/components/A/ASelect/ASelect'
import AInput from '@/components/A/AInput/AInput'
import cls from 'classnames'
import AUpload, { Props as AUploadProps } from '@/components/A/AUpload/AUpload'
// import ADateRangePicker from '@/components/A/ADateRangePicker/ADateRangePicker'
import AInputNumberRange from '@/components/A/AInputNumberRange/AInputNumberRange'
import VideoPlayer from '@/pages/ProjectDetailPage/components/VideoAnalysis/VideoPlayer/VideoPlayer'
import { Popover, Spin, message } from 'antd'
import ACascader from '@/components/A/ACascader/ACascader'
import { useTranslation } from 'react-i18next'
import MiddleText from '@/components/MiddleText/MiddleText'
import { useStepOptions } from './useStepOptions'
import { SUPPORTED_VIDEO_EXT } from '@/services/uploadService'
import DropdownSelector from '@/components/ApplyTrialBox/components/DropdownSelector/DropdownSelector'
import useProductType from '@/helpers/commonHooks/useProductType'
import {
  isQuickProductCategory,
  isPortraitProductCategory,
  isSurveyProductCategory,
  toProjectType,
  toVideoDurationProjectCategory,
} from '@/helpers/productCategory'
import { useRequest } from 'alova/client'
import { api } from '@/api'
import {
  resolveOutputLanguageByI18nLanguage,
  SURVEY_OUTPUT_LANGUAGE_OPTIONS,
} from '@/helpers/outputLanguage'
import { INTERNAL_HOST } from '@/commonConst'

type AdaptParamWithNull<T> = {
  [K in keyof T]: T[K] | null
}

const DEFAULT_PROJECT_CRREATE_PARAM: AdaptParamWithNull<ProjectModels.CreateProjectParam> = {
  projectType: 'ad_creative_test',
  projectName: null,
  brandIndustry: null,
  brandName: null,
  fileId: null,
  adUrl: null,
  adName: null,
  norm: null,
  // adCampaignDurationStart: null,
  // adCampaignDurationEnd: null,
  adTargetMarket: null,
  adCustomerGender: null,
  adCustomerAgeStart: null,
  adCustomerAgeEnd: null,
  projectCategory: 'horizontal-base',
  // adVideoLanguage: null,
  // adCharacteristicTags: null,

}

const PORTRAIT_DURATION_CATEGORY_OPTIONS: Array<{
  value: ProjectModels.DurationCategory
  labelKey: string
}> = [
    { value: 'ten_plus', labelKey: 'dashboard.projectCreatePage.durationCategoryOptions.tenPlus' },
    { value: 'five_to_ten', labelKey: 'dashboard.projectCreatePage.durationCategoryOptions.fiveToTen' },
  ]
const PORTRAIT_SPLASH_MIN_UPLOAD_DURATION = 4
const PORTRAIT_SPLASH_MAX_UPLOAD_DURATION = 10
const INTERNAL_DEFAULT_OUTPUT_LANGUAGE: ProjectModels.UseLanguage = 'CN'
const OVERSEAS_DEFAULT_OUTPUT_LANGUAGE: ProjectModels.UseLanguage = 'EN'

function withDefaultOutputLanguage(
  param: AdaptParamWithNull<ProjectModels.CreateProjectParam>,
  outputLanguage: ProjectModels.UseLanguage | null
) {
  return outputLanguage && !param.useLanguage
    ? { ...param, useLanguage: outputLanguage }
    : param
}

function withOutputLanguage(
  param: AdaptParamWithNull<ProjectModels.CreateProjectParam>,
  outputLanguage: ProjectModels.UseLanguage | null
) {
  return outputLanguage
    ? { ...param, useLanguage: outputLanguage }
    : param
}

function StepOne(props: {
  className?: string
  onNext: (param: ProjectModels.CreateProjectParam) => void
}) {
  const { t, i18n } = useTranslation()
  const { productType } = useProductType()
  const isPortraitMode = isPortraitProductCategory(productType)
  const isSurveyMode = isSurveyProductCategory(productType)
  const isCreativeDiagnosisMode = !isQuickProductCategory(productType) && !isSurveyMode
  const shouldShowOutputLanguage = isSurveyProductCategory(productType)
  const defaultOutputLanguage = INTERNAL_HOST.includes(location.hostname)
    ? INTERNAL_DEFAULT_OUTPUT_LANGUAGE
    : OVERSEAS_DEFAULT_OUTPUT_LANGUAGE
  const creativeDiagnosisOutputLanguage = isCreativeDiagnosisMode
    ? resolveOutputLanguageByI18nLanguage(i18n.language, defaultOutputLanguage)
    : null
  const { GENDER_OPTIONS, TARGET_MARKET_OPTIONS, NORM_OPTIONS, INDUSTRY_CATEGORY_OPTIONS, OUTPUT_LANGUAGE_OPTIONS } = useStepOptions(SURVEY_OUTPUT_LANGUAGE_OPTIONS)
  const portraitFieldControlClassName = isPortraitMode ? styles.portraitFieldControl : undefined

  const [uploadData, setUploadData] = useState<{
    status: 'not_begin' | 'error' | 'pending' | 'success'
    fileUrl: string
    errMsg: string
  }>({ status: 'not_begin', fileUrl: '', errMsg: '' })
  const [uploadedVideoDuration, setUploadedVideoDuration] = useState<number | null>(null)
  const [projectCreateParam, setProjectCreateParam] = useState<
    AdaptParamWithNull<ProjectModels.CreateProjectParam>
  >(() => {
    const baseParam = withDefaultOutputLanguage({
      ...DEFAULT_PROJECT_CRREATE_PARAM,
      projectType: toProjectType(productType),
      projectCategory: productType,
    }, shouldShowOutputLanguage ? null : creativeDiagnosisOutputLanguage)
    if (isPortraitProductCategory(productType)) {
      return { ...baseParam, durationCategory: 'ten_plus' }
    }
    return baseParam
  })

  const backendProjectCategory = toVideoDurationProjectCategory(projectCreateParam.projectCategory || productType)
  const { data: videoDurationConfig } = useRequest(
    () => api.corp.getCorpVideoDurationConfig({ projectCategory: backendProjectCategory }),
    {
      initialData: {
        projectCategory: backendProjectCategory,
        minDuration: 10,
        maxDuration: 120,
        config: {
          quickLandscape: { minDuration: 10, maxDuration: 120 },
          base: { minDuration: 10, maxDuration: 120 },
          survey: { minDuration: 10, maxDuration: 120 },
          quickPortrait: { minDuration: 10, maxDuration: 120 },
          basePortrait: { minDuration: 10, maxDuration: 120 },
          surveyPortrait: { minDuration: 10, maxDuration: 120 },
        },
      },
      watchingStates: [backendProjectCategory],
    }
  )

  const [errorKeys, setErrorKeys] = useState<
    Array<keyof AdaptParamWithNull<ProjectModels.CreateProjectParam>>
  >([])

  /*   const [genderOptions, setGenderOptions] =
    useState<Array<{ value: string; label: string }>>(GENDER_OPTIONS)
  const [industryCategoryOptions, setIndustryCategoryOptions] =
    useState<
      Array<{ value: string; label: string; children: Array<{ value: string; label: string }> }>
    >(INDUSTRY_CATEGORY_OPTIONS)
  const [targetMarketOptions, setTargetMarketOptions] =
    useState<Array<{ value: string; label: string }>>(TARGET_MARKET_OPTIONS) */

  const onUploadChange: AUploadProps['onUploadChange'] = (p: {
    status: 'error' | 'pending' | 'success'
    fileUrl: string
    fileId: number
    errMsg: string
    duration?: number
  }) => {

    setUploadData(p)
    if (p.status === 'success') {
      setUploadedVideoDuration(p.duration ?? null)
      setProjectCreateParam((param) => ({ ...param, adUrl: p.fileUrl, fileId: p.fileId }))
    }
    if (p.status === 'error') {
      setUploadedVideoDuration(null)
      setUploadData((p) => ({ ...p, errMsg: p.errMsg }))
      message.error(p.errMsg)
    }
    if (p.status === 'pending') {
      setUploadedVideoDuration(null)
    }
  }
  const handleRemoveVideo: MouseEventHandler<HTMLSpanElement> = (event) => {
    event.stopPropagation()
    setUploadedVideoDuration(null)
    setUploadData({ status: 'not_begin', fileUrl: '', errMsg: '' })
  }

  const validate = (force: boolean = false) => {
    let validateKeys: Array<keyof AdaptParamWithNull<ProjectModels.CreateProjectParam>> = errorKeys
    for (const key in projectCreateParam) {
      const k = key as keyof AdaptParamWithNull<ProjectModels.CreateProjectParam>
      const v = projectCreateParam[k]
      const notValidateInitialValue = force ? true : v !== null
      const vIsEmpty =
        v === undefined || v === 0 || v === '' || v === null || (Array.isArray(v) && v.length === 0)
      const special = k === 'adCustomerAgeStart' && v === 0
      if (notValidateInitialValue && vIsEmpty && !special) {
        validateKeys = [...validateKeys, k]
      } else {
        validateKeys = validateKeys.filter((key) => key !== k)
      }
    }
    /** 单独校验一下 age 的 range 判断 */
    if (
      !validateKeys.includes('adCustomerAgeEnd')
    ) {
      const startTime = projectCreateParam['adCustomerAgeStart']!
      const endTime = projectCreateParam['adCustomerAgeEnd']!
      /** 起始时间 > 截止时间 */
      if (startTime > endTime) {
        validateKeys.push('adCustomerAgeEnd')
      } else {
        const AGE_RANGE = [20, 55]
        if (force && (startTime < AGE_RANGE[0] || endTime > AGE_RANGE[1])) {
          validateKeys.push('adCustomerAgeStart')
        }
      }
    }
    if (
      shouldShowOutputLanguage
      && !projectCreateParam.useLanguage
      && (force || errorKeys.includes('useLanguage'))
    ) {
      validateKeys = [...validateKeys, 'useLanguage']
    } else {
      validateKeys = validateKeys.filter((key) => key !== 'useLanguage')
    }
    return validateKeys
  }

  const handleNext = () => {
    const keys = Array.from(new Set(validate(true)))
    setErrorKeys(keys)
    if (keys.length > 0) {
      if (keys.length === 2 && keys.includes('adUrl') && keys.includes('fileId')) {
        return message.warning(t('dashboard.projectCreatePage.message.uploadAdVideo'))
      } else {
        return message.warning(t('dashboard.projectCreatePage.message.formNotCompleted'))
      }
    }
    const param = isCreativeDiagnosisMode
      ? withOutputLanguage(projectCreateParam, creativeDiagnosisOutputLanguage)
      : projectCreateParam
    props.onNext(param as ProjectModels.CreateProjectParam)
  }

  const portraitDurationCategory = projectCreateParam.durationCategory || 'ten_plus'
  const portraitGeneralDurationError = t('dashboard.projectCreatePage.message.videoDurationNeedOver10s')
  const portraitSplashDurationError = t('dashboard.projectCreatePage.message.videoDurationNeedWithin5To10s')
  const portraitTypeSwitchDurationError = t('dashboard.projectCreatePage.message.videoDurationNotMatchDurationCategory')
  const portraitTypeSwitchUploadingError = t('dashboard.projectCreatePage.message.videoUploadingCannotSwitchDurationCategory')
  const isPortraitGeneralCreative = portraitDurationCategory === 'ten_plus'
  const validatePortraitDuration = (duration: number, durationCategory: ProjectModels.DurationCategory) => {
    if (durationCategory === 'ten_plus') {
      return duration > 10
    }
    return duration >= PORTRAIT_SPLASH_MIN_UPLOAD_DURATION && duration <= PORTRAIT_SPLASH_MAX_UPLOAD_DURATION
  }

  const uploadMinDuration = isPortraitMode
    ? isPortraitGeneralCreative ? 10 : PORTRAIT_SPLASH_MIN_UPLOAD_DURATION
    : videoDurationConfig.minDuration
  const uploadMaxDuration = isPortraitMode
    ? isPortraitGeneralCreative ? videoDurationConfig.maxDuration + 1 : PORTRAIT_SPLASH_MAX_UPLOAD_DURATION
    : videoDurationConfig.maxDuration + 1
  const maxDurationErrorMsg = isPortraitMode && !isPortraitGeneralCreative
    ? portraitSplashDurationError
    : t('dashboard.projectCreatePage.message.videoDurationOver60s', {
      max: videoDurationConfig.maxDuration,
    })
  const minDurationErrorMsg = isPortraitMode
    ? isPortraitGeneralCreative
      ? portraitGeneralDurationError
      : portraitSplashDurationError
    : t('dashboard.projectCreatePage.message.videoDurationLessThan10s', {
      min: videoDurationConfig.minDuration,
    })
  const uploadDesc1 = isPortraitMode
    ? t('dashboard.projectCreatePage.message.uploadDescPortrait', {
      defaultValue: t('dashboard.projectCreatePage.message.uploadDesc1'),
    })
    : t('dashboard.projectCreatePage.message.uploadDesc1')
  const industryCascaderValue = (() => {
    const brandIndustry = projectCreateParam['brandIndustry']
    if (!brandIndustry) {
      return undefined
    }
    const [primary, ...rest] = brandIndustry.split('-')
    if (!primary || rest.length === 0) {
      return [brandIndustry]
    }
    return [primary, rest.join('-')]
  })()

  useEffect(() => {
    const keys = Array.from(new Set(validate()))
    setErrorKeys(keys)
  }, [projectCreateParam])

  return (
    <div className={cls(styles.stepOne, { [styles.stepOnePortrait]: isPortraitMode }, props.className)}>
      <div className={styles.uploadBox}>
        <AUpload
          testId='project-create-video-upload'
          // accept="video/mp4,video/mov,video/ogg,video/x-m4v,video/quicktime"
          accept={SUPPORTED_VIDEO_EXT.map((ext) => `video/${ext}`).join(',')}
          source='project_create'
          expectOrientation={isPortraitMode ? 'portrait' : 'landscape'}
          /** 视频文件大小限制: 256MB */
          maxSize={256}
          minDuration={uploadMinDuration}
          maxDuration={uploadMaxDuration}
          minDurationErrorMsg={minDurationErrorMsg}
          maxDurationErrorMsg={maxDurationErrorMsg}
          validateDuration={(duration) => {
            if (!isPortraitMode) {
              return undefined
            }
            if (portraitDurationCategory === 'ten_plus' && duration <= 10) {
              return portraitGeneralDurationError
            }
            if (portraitDurationCategory === 'ten_plus' && duration < videoDurationConfig.minDuration) {
              return minDurationErrorMsg
            }
            if (
              portraitDurationCategory === 'five_to_ten' &&
              (duration < PORTRAIT_SPLASH_MIN_UPLOAD_DURATION || duration > PORTRAIT_SPLASH_MAX_UPLOAD_DURATION)
            ) {
              return portraitSplashDurationError
            }
            return undefined
          }}
          onUploadChange={onUploadChange}
        >
          <div className={styles.uploadContainer}>
            {(uploadData.status === 'not_begin' || uploadData.status === 'error') && (
              <div className={styles.uploadInner}>
                <img
                  className={styles.coverPic}
                  src={commonUtils.getImagePath('projectCreatePage/upload-cover-pic.png')}
                  alt=''
                />
                {uploadData.status === 'not_begin' && (
                  <p className={styles.boldDesc}>
                    {t('dashboard.projectCreatePage.message.clickToUploadTestVideo')}
                  </p>
                )}
                {uploadData.status === 'error' && (
                  <p className={cls(styles.boldDesc, styles.error)}>
                    {t('dashboard.projectCreatePage.message.uploadFailed')}
                  </p>
                )}
                <p className={styles.subDesc}>
                  {uploadDesc1}
                </p>
                <p className={styles.subDesc}>
                  {t('dashboard.projectCreatePage.message.uploadDesc2')}
                </p>
              </div>
            )}
            {uploadData.status === 'pending' && (
              <div className={styles.uploadInner}>
                <Spin className={styles.uploadPending} />
              </div>
            )}
            {uploadData.status === 'success' && (
              <div className={cls(styles.uploadInner, styles.success)}>
                <VideoPlayer url={uploadData.fileUrl} />
                <span
                  className={styles.deleteVideoBtn}
                  onClick={handleRemoveVideo}
                  data-testid='project-create-video-remove-btn'
                >
                  <AIcon type='icon-line-global_close1'></AIcon>
                </span>
              </div>
            )}
          </div>
        </AUpload>
      </div>
      <div className={styles.formArea}>
        <div className={styles.formBox}>
          <FormTitle text={t('dashboard.projectCreatePage.basicInformation')} />
          {isPortraitMode && (
            <FormRow>
              <FormItem label={t('dashboard.projectCreatePage.durationCategory')} required>
                <div className={styles.durationCategoryBox}>
                  {PORTRAIT_DURATION_CATEGORY_OPTIONS.map((option) => {
                    const checked = projectCreateParam.durationCategory === option.value
                    return (
                      <button
                        type='button'
                        key={option.value}
                        data-testid={`project-create-duration-category-${option.value}`}
                        className={cls(styles.durationCategoryOption, { [styles.active]: checked })}
                        onClick={() => {
                          if (checked) {
                            return
                          }
                          if (uploadData.status === 'pending') {
                            message.warning(portraitTypeSwitchUploadingError)
                            return
                          }
                          if (
                            uploadData.status === 'success' &&
                            uploadedVideoDuration !== null &&
                            !validatePortraitDuration(uploadedVideoDuration, option.value)
                          ) {
                            message.warning(portraitTypeSwitchDurationError)
                            return
                          }
                          setProjectCreateParam((p) => ({ ...p, durationCategory: option.value }))
                        }}
                      >
                        {checked && (
                          <>
                            <img
                              className={styles.durationCheckCover}
                              src={commonUtils.getImagePath('projectCreatePage/type-check.png')}
                              alt=''
                            />
                            <span className={styles.durationCheckIcon}>
                              <AIcon width={16} height={16} type='icon-line-global_check' />
                            </span>
                          </>
                        )}
                        <span className={styles.durationOptionText}>{t(option.labelKey)}</span>
                      </button>
                    )
                  })}
                </div>
                <p className={styles.durationCategoryDesc}>
                  {t('dashboard.projectCreatePage.durationCategoryDesc')}
                </p>
              </FormItem>
            </FormRow>
          )}
          <FormRow>
            <FormItem
              label={t('dashboard.projectCreatePage.projectName')}
              error={errorKeys.includes('projectName')}
              required
            >
              <AInput
                testId='project-name-input'
                className={portraitFieldControlClassName}
                maxLength={40}
                showCount
                placeholder={t('dashboard.projectCreatePage.placeholder.projectName')}
                onChange={(e) => {
                  setProjectCreateParam((p) => ({ ...p, projectName: e.target.value }))
                }}
              />
            </FormItem>
            <FormItem
              label={t('dashboard.projectCreatePage.categoryType')}
              error={errorKeys.includes('brandIndustry')}
              required
            >
              <ACascader
                testId='brand-industry-cascader'
                className={portraitFieldControlClassName}
                options={INDUSTRY_CATEGORY_OPTIONS}
                placeholder={t('dashboard.projectCreatePage.placeholder.category')}
                value={industryCascaderValue}
                showSearch={{
                  filter: (inputValue, path) => {
                    return path.some((option) => (option.label as string).toLowerCase().indexOf(inputValue.toLowerCase()) > -1)
                  },
                  render: (inputValue, path) => {
                    return path.map((option, i) => {
                      const label = option.label as string
                      const index = label.toLowerCase().indexOf(inputValue.toLowerCase());
                      return <span key={inputValue + i}>
                        {
                          index === -1
                            ? label
                            : <>
                              {label.substring(0, index)}
                              <span style={{ color: '#6D54FA' }}>
                                {label.substring(index, index + inputValue.length)}
                              </span>
                              {label.substring(index + inputValue.length)}
                            </>
                        }
                        {i !== path.length - 1 && '/'}
                      </span>
                    })
                  }
                }}
                onChange={(value) => {
                  if (value !== undefined) {
                    const v = value as string[]
                    if (v.length >= 2) {
                      setProjectCreateParam((p) => ({ ...p, brandIndustry: `${v[0]}-${v[1]}` }))
                    } else if (v.length === 1) {
                      setProjectCreateParam((p) => ({ ...p, brandIndustry: v[0] }))
                    } else {
                      setProjectCreateParam((p) => ({ ...p, brandIndustry: null }))
                    }
                  } else {
                    setProjectCreateParam((p) => ({ ...p, brandIndustry: null }))
                  }
                }}
              />
            </FormItem>
          </FormRow>
          <FormRow style={{ marginBottom: 32 }}>
            <FormItem
              label={t('dashboard.projectCreatePage.brandName')}
              error={errorKeys.includes('brandName')}
              required
            >
              <AInput
                testId='brand-name-input'
                className={portraitFieldControlClassName}
                maxLength={40}
                showCount
                placeholder={t('dashboard.projectCreatePage.placeholder.brandName')}
                onChange={(e) => {
                  setProjectCreateParam((p) => ({ ...p, brandName: e.target.value }))
                }}
              />
            </FormItem>
            <FormItem
              label={t('dashboard.projectCreatePage.norm')}
              error={errorKeys.includes('norm')}
              required
            >
              <ASelect
                testId='norm-select'
                className={portraitFieldControlClassName}
                placeholder={t('dashboard.projectCreatePage.placeholder.norm')}
                options={NORM_OPTIONS.map(opt => {
                  if (opt.value === 2) {
                    /** 品牌基准值 */
                    const label: React.ReactNode = <span>
                      <span>{opt.label}</span>
                      <Popover
                        overlayClassName={styles.normPopover}
                        arrow={{ pointAtCenter: true }}
                        placement='top'
                        content={
                          <p className={styles.normDesc}>{t('dashboard.projectCreatePage.message.normDesc')}</p>
                        }
                      >
                        <span className={styles.normPopoverIcon}>
                          <AIcon type='icon-line-global_question' width={16} height={16}></AIcon>
                        </span>
                      </Popover>
                    </span>
                    return { ...opt, label }
                  } else {
                    return opt
                  }
                })}
                labelRender={(option) => {
                  return NORM_OPTIONS.find((opt) => opt.value === option.value)?.label ?? option.label
                }}
                value={projectCreateParam['norm']}
                onChange={(value) => {
                  setProjectCreateParam((p) => ({ ...p, norm: value }))
                }}
              ></ASelect>
            </FormItem>
          </FormRow>
          <FormTitle text={t('dashboard.projectCreatePage.advertisingInformation')} />
          <FormRow style={{ marginTop: 19 }}>
            <FormItem
              label={t('dashboard.projectCreatePage.adName')}
              error={errorKeys.includes('adName')}
              required
            >
              <AInput
                testId='ad-name-input'
                className={portraitFieldControlClassName}
                maxLength={40}
                showCount
                placeholder={t('dashboard.projectCreatePage.placeholder.adName')}
                onChange={(e) => {
                  setProjectCreateParam((p) => ({ ...p, adName: e.target.value }))
                }}
              />
            </FormItem>
            <FormItem
              label={t('dashboard.projectCreatePage.targetMarket')}
              error={errorKeys.includes('adTargetMarket')}
              required
            >
              <DropdownSelector
                testId='target-market-selector'
                className={portraitFieldControlClassName}
                showSearch
                placeholder={t('dashboard.projectCreatePage.placeholder.targetMarket')}
                otherPlaceholder={t('modals.freeTrialModal.applyBox.pleaseEnter')}
                options={TARGET_MARKET_OPTIONS}
                onChange={(item) => {
                  let adTargetMarket = ''
                  if (item.value === 'Other') {
                    adTargetMarket = item.label
                  } else {
                    adTargetMarket = commonUtils.transferCountryKeyToZhTranslateText(item.value)
                  }
                  setProjectCreateParam((p) => ({ ...p, adTargetMarket }))
                }}
              >
              </DropdownSelector>
            </FormItem>
          </FormRow>
          <FormRow style={{ marginBottom: shouldShowOutputLanguage ? 32 : 0 }}>
            <FormItem
              label={t('dashboard.projectCreatePage.taGender')}
              error={errorKeys.includes('adCustomerGender')}
              required
            >
              <ASelect
                testId='ta-gender-select'
                className={portraitFieldControlClassName}
                placeholder={t('dashboard.projectCreatePage.placeholder.taGender')}
                options={GENDER_OPTIONS}
                value={projectCreateParam['adCustomerGender']}
                onChange={(value) => {
                  setProjectCreateParam((p) => ({ ...p, adCustomerGender: value }))
                }}
              ></ASelect>
            </FormItem>
            <FormItem
              label={t('dashboard.projectCreatePage.taAge')}
              error={errorKeys.includes('adCustomerAgeEnd') || errorKeys.includes('adCustomerAgeStart')}
              // 如果是非法输入，怎么采用原始的提示，如果是不在范围内，则展示另外的提示
              errorMsg={
                errorKeys.includes('adCustomerAgeEnd') ? undefined :
                  errorKeys.includes('adCustomerAgeStart') ? t('dashboard.projectCreatePage.message.ageRangeInvalid') : undefined}
              required
            >
              <AInputNumberRange
                testId='ta-age-range'
                classNames={portraitFieldControlClassName}
                onChange={(range) => {
                  setProjectCreateParam((p) => ({
                    ...p,
                    adCustomerAgeStart: Number(range[0]),
                    adCustomerAgeEnd: Number(range[1]),
                  }))
                }}
              />
            </FormItem>
          </FormRow>
          {
            shouldShowOutputLanguage && <>
              <FormTitle text={t('dashboard.projectCreatePage.outputLanguage')}></FormTitle>
              <FormRow>
                <FormItem
                  label={t('dashboard.projectCreatePage.outputLanguage')}
                  error={errorKeys.includes('useLanguage')}
                  required
                >
                  <ASelect
                    testId='output-language-select'
                    className={portraitFieldControlClassName}
                    placeholder={t('dashboard.projectCreatePage.placeholder.outputLanguage')}
                    options={OUTPUT_LANGUAGE_OPTIONS}
                    value={projectCreateParam['useLanguage'] || undefined}
                    onChange={(value) => {
                      setProjectCreateParam((p) => ({ ...p, useLanguage: value }))
                    }}
                  ></ASelect>
                </FormItem>
              </FormRow>
            </>
          }
          {/*           <FormItem label='Characteristic Tags' error={errorKeys.includes('adCharacteristicTags')}>
            <ASelect
              placeholder='Select the characteristic tag'
              mode='multiple'
              options={characteristicTagsOptions}
              onChange={(value) => {
                setProjectCreateParam((p) => ({ ...p, adCharacteristicTags: value }))
              }}
            ></ASelect>
          </FormItem> */}

          {/*         <FormRow>
          <FormItem label='Video Language' error={errorKeys.includes('adVideoLanguage')}>
            <ASelect
              placeholder='Select the primary language'
              options={languageOptions}
              onChange={(value) => {
                setProjectCreateParam((p) => ({ ...p, adVideoLanguage: value }))
              }}
            ></ASelect>
          </FormItem>
        </FormRow> */}
          {/*           <FormItem
            label='Ad Campaign Duration'
            error={errorKeys.includes('adCampaignDurationEnd')}
          >
            <ADateRangePicker
              onChange={(range) => {
                const [start, end] = range || []
                const adCampaignDurationStart = start ? start.unix() : 0
                const adCampaignDurationEnd = end ? end.unix() : 0
                setProjectCreateParam((p) => ({
                  ...p,
                  adCampaignDurationStart,
                  adCampaignDurationEnd,
                }))
              }}
            ></ADateRangePicker>
          </FormItem> */}
        </div>
        <div className={styles.actionBox}>
          <AButton testId='project-create-next-btn' size='LG' type='primary' onClick={handleNext}>
            <MiddleText text={t('dashboard.projectCreatePage.next')} />
            <AIcon type='icon-line-global_arrow'></AIcon>
          </AButton>
        </div>
      </div>
    </div>
  )
}

export function FormRow(props: { children: React.ReactNode; className?: string; style?: CSSProperties }) {
  return (
    <div className={cls(styles.formRow, props.className)} style={props.style}>
      {props.children}
    </div>
  )
}

export function FormItem(props: {
  label?: string
  error?: boolean
  errorMsg?: string
  errorMsgRender?: (errorMsg: string) => React.ReactNode
  required?: boolean
  children?: React.ReactNode
  labelRender?: (label: string) => React.ReactNode
}) {
  const { t } = useTranslation()
  return (
    <div className={styles.formItem}>
      {props.label && (
        <div className={cls(styles.formItemLabel, { [styles.required]: props.required })}>
          {props.labelRender ? props.labelRender(props.label) : <label>{props.label}: </label>}
        </div>
      )}
      <div className={cls(styles.formItemContent, { [styles.error]: props.error })}>
        <div className={cls(styles.formItemContentInner, { [styles.error]: props.error })}>
          {props.children}
        </div>
        {props.error && (
          props.errorMsgRender
            ? props.errorMsgRender(props.errorMsg || t('dashboard.projectCreatePage.message.inputInvalid')) :
            <span className={styles.errorMsg}>
              {props.errorMsg || t('dashboard.projectCreatePage.message.inputInvalid')}
            </span>
        )}
      </div>
    </div>
  )
}

export function FormTitle(props: {
  text: string | React.ReactNode
  required?: boolean
  style?: CSSProperties
}) {
  return (
    <div className={styles.formTitle} style={props.style}>
      {props.required && <span className={styles.required}>*</span>}
      <span className={styles.text}>{props.text}</span>
    </div>
  )
}

export default StepOne
